import type { ComboboxOption } from '@atlas/ui-primitives'
import { useState } from 'react'
import type { CreateMessageInput, Message, MessageListParams } from '@/domain/message.types'
import type { Selection } from '@/domain/work-order.types'
import { useClassifyBatch } from '@/hooks/useClassification'
import type { RailAxis } from '@/hooks/useLibrary'
import { useCreateMessage } from '@/hooks/useMessages'
import type { ClassifyTuple } from '@/services/classification.service'
import { toMessageTuples } from './classification'
import { EMPTY_FORM, type MessageFormState, toCreateInput } from './form-model'

/**
 * A copied selection is the draft's own from the moment it opens - editing the
 * chips must never reach the surface it came from.
 *
 * Narrowed to the axes the picker offers: the seed comes from every tag resolved
 * on the surrounding surface, while the picker renders only pickable axes. An
 * axis outside that set would be submitted to `classifyBatch` with no chip on
 * screen to show or remove it, and would fail the call outright if it is not
 * valid for a Message.
 */
const seedSelection = (selection: Selection, axes: RailAxis[]): Selection => {
	const pickable = new Set(axes.map((axis) => axis.code))
	return Object.fromEntries(
		Object.entries(selection)
			.filter(([axisCode]) => pickable.has(axisCode))
			.map(([axisCode, valueCodes]) => [axisCode, [...valueCodes]]),
	)
}

/**
 * A new row starts inside the view the user is looking at: its type and status
 * come from the page's own filters, its source locale from the grid's first
 * column (a source locale, since `buildLocaleColumns` leads with those), and its
 * classification from whatever the surrounding surface already answers for — a
 * Campaign's brand, market and objective are its copy's too. Without the first
 * two, a row created under an active filter would be added and then immediately
 * filtered out of the grid it was added to; without the last, every axis the
 * surface already states would have to be picked again by hand.
 */
const seedDraft = (
	filters: MessageListParams,
	defaultLocale: string,
	seedClassification: Selection,
	axes: RailAxis[],
): MessageFormState => ({
	...EMPTY_FORM,
	languageIso: defaultLocale || EMPTY_FORM.languageIso,
	messageType: filters.messageType ?? EMPTY_FORM.messageType,
	status: filters.status ?? EMPTY_FORM.status,
	classification: seedSelection(seedClassification, axes),
})

/**
 * A row lands in up to three writes — the message, its classification, and
 * whatever attaches it to the surface it was added from — so a created message
 * can outlive either of the two that follow it. The id comes back either way, so
 * a retry passes it as `existingMessageId` and re-runs only what is left rather
 * than creating a second copy of the same row; both follow-ups union server-side,
 * so re-running one that already succeeded changes nothing.
 */
async function writeRow({
	draft,
	existingMessageId,
	create,
	classify,
	attach,
}: {
	draft: MessageFormState
	existingMessageId: string | null
	create: (body: CreateMessageInput) => Promise<Message>
	classify: (tuples: ClassifyTuple[]) => Promise<unknown>
	attach: MatrixAddRowAttachment | undefined
}): Promise<{ messageId: string | null; error: string | null }> {
	let messageId = existingMessageId
	if (!messageId) {
		try {
			messageId = (await create(toCreateInput(draft))).messageId
		} catch {
			return { messageId: null, error: 'Could not add the message. Check the fields and try again.' }
		}
	}
	const tuples = toMessageTuples(messageId, draft.classification)
	if (tuples.length > 0) {
		try {
			await classify(tuples)
		} catch {
			return { messageId, error: 'The message was added, but its classification did not save — retry to finish it.' }
		}
	}
	if (!attach) return { messageId, error: null }
	try {
		await attach.run(messageId)
		return { messageId, error: null }
	} catch {
		return { messageId, error: attach.failureMessage }
	}
}

/**
 * How a new row is attached to the surface it was added from — a Campaign links
 * it to itself. The surface supplies its own `failureMessage`, since the grid does
 * not know what it is embedded in and cannot name it.
 */
export interface MatrixAddRowAttachment {
	run: (messageId: string) => Promise<void>
	failureMessage: string
}

export interface MatrixAddRowInput {
	/** The page's own filters — what a new row is seeded to so it lands in the current view. */
	filters: MessageListParams
	axes: RailAxis[]
	localeOptions: ComboboxOption[]
	/** The grid's first locale column, offered as the new row's source locale. */
	defaultLocale: string
	/** Classification the embedding surface already answers for, copied onto the draft. */
	seedClassification?: Selection
	attach?: MatrixAddRowAttachment
}

/** The draft surface itself: whether it is open, what is in it, and what it opens seeded with. */
function useRowDraft(
	filters: MessageListParams,
	defaultLocale: string,
	seedClassification: Selection | undefined,
	axes: RailAxis[],
) {
	const [isOpen, setIsOpen] = useState(false)
	const [draft, setDraft] = useState<MessageFormState>(EMPTY_FORM)

	return {
		isOpen,
		draft,
		open: () => {
			setDraft(seedDraft(filters, defaultLocale, seedClassification ?? {}, axes))
			setIsOpen(true)
		},
		close: () => setIsOpen(false),
		set: <K extends keyof MessageFormState>(key: K, value: MessageFormState[K]) =>
			setDraft((prev) => ({ ...prev, [key]: value })),
	}
}

/**
 * Adding a row to the resolution matrix: one new source message, classified as
 * it is created and attached to the surface it was added from. Only the source
 * row is written — its locale columns are filled afterwards by the grid's own AI
 * translation or row editing, which is what the grid is for.
 */
export function useMatrixAddRow(input: MatrixAddRowInput) {
	const { filters, axes, localeOptions, defaultLocale, seedClassification, attach } = input
	const create = useCreateMessage()
	const classify = useClassifyBatch()
	const editor = useRowDraft(filters, defaultLocale, seedClassification, axes)
	const [createdMessageId, setCreatedMessageId] = useState<string | null>(null)
	const [saveError, setSaveError] = useState<string | null>(null)
	// Spans the whole write rather than reading the two mutations' own pending
	// flags: the attach step is the caller's mutation, so those two report idle
	// while it is still in flight — long enough for a second submit to slip in.
	const [isSaving, setIsSaving] = useState(false)

	const cancel = () => {
		editor.close()
		setCreatedMessageId(null)
		setSaveError(null)
	}

	const submit = async () => {
		if (isSaving) return
		setSaveError(null)
		setIsSaving(true)
		const outcome = await writeRow({
			draft: editor.draft,
			existingMessageId: createdMessageId,
			create: create.mutateAsync,
			classify: classify.mutateAsync,
			attach,
		})
		setIsSaving(false)
		if (!outcome.error) return cancel()
		setCreatedMessageId(outcome.messageId)
		setSaveError(outcome.error)
	}

	return {
		isOpen: editor.isOpen,
		open: () => {
			editor.open()
			setCreatedMessageId(null)
			setSaveError(null)
		},
		cancel,
		draft: editor.draft,
		set: editor.set,
		axes,
		localeOptions,
		submit,
		isSaving,
		saveError,
	}
}
